import type { Board, GenerateOptions } from '../core/board';
import { createId } from './identity';

export type BoardRequest = Omit<GenerateOptions, 'seed'>;
type Generator = (options: GenerateOptions, signal: AbortSignal) => Promise<Board>;

/** Disposable, memory-only cache. A cache miss always falls back to normal generation. */
export class PreparedBoards {
  private key = '';
  private boards: Board[] = [];
  private pending?: AbortController;
  private epoch = 0;
  get size(): number { return this.boards.length; }

  constructor(private generate: Generator) {}

  clear(): void {
    this.epoch++;
    this.pending?.abort(); this.pending = undefined;
    this.boards = []; this.key = '';
  }

  private configure(request: BoardRequest): void {
    // Include actual pack content and exclusions, not just mutable pack IDs.
    const key = JSON.stringify(request);
    if (key === this.key) return;
    this.clear(); this.key = key;
  }

  take(request: BoardRequest): Board | undefined {
    this.configure(request);
    return this.boards.shift();
  }

  async prepare(request: BoardRequest): Promise<void> {
    this.configure(request);
    if (this.pending || this.boards.length >= 2) return;
    const controller = new AbortController(), epoch = this.epoch;
    const options = structuredClone(request);
    this.pending = controller;
    try {
      // Only one speculative worker runs at a time, and only two boards are retained.
      while (this.boards.length < 2 && !controller.signal.aborted) {
        const board = await this.generate({ ...options, seed: createId() }, controller.signal);
        if (epoch !== this.epoch || controller.signal.aborted) return;
        this.boards.push(board);
      }
    } catch { /* Optional preparation never prevents a fresh foreground attempt. */ }
    finally { if (this.pending === controller) this.pending = undefined; }
  }
}
